// Shared constants, validation helpers, and small style/UI helpers used by the
// Pawn Customer Master screen and its extracted sub-components. Kept in one
// module so there is a single source of truth (importing them from the main
// component instead would create a circular import).
import React from "react";
import { Typography } from "@mui/material";

export const PRIMARY = "#2563EB";
export const BG = "#F8FAFC";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

// ─── Validation helpers ───────────────────────────────────────────────────────────
export const validateMobile = (value) => {
  const cleaned = value?.toString().replace(/\D/g, "") || "";
  if (cleaned.length === 0) return { isValid: true, value: "", error: "" };
  if (cleaned.length > 10)
    return {
      isValid: false,
      value: cleaned.slice(0, 10),
      error: "Mobile number must be exactly 10 digits",
    };
  if (cleaned.length === 10)
    return { isValid: true, value: cleaned, error: "" };
  return { isValid: true, value: cleaned, error: "" };
};

// ─── Mobile number entry ──────────────────────────────────────────────────────
// An Indian mobile number is exactly 10 digits, so the entry fields refuse
// everything else outright rather than validating after the fact.
export const MOBILE_MAX_DIGITS = 10;

// Reduces anything that reached the field — typed, pasted, autofilled or from a
// soft keyboard — to at most 10 digits. Over-long input has a pasted country /
// trunk prefix dropped first, so pasting "+91 98765 43210" keeps the number
// instead of silently eating its last three digits.
export const sanitizeMobileInput = (value, maxDigits = MOBILE_MAX_DIGITS) => {
  let digits = (value ?? "").toString().replace(/\D/g, "");
  if (digits.length > maxDigits) {
    if (digits.length === 13 && digits.startsWith("091")) digits = digits.slice(3);
    else if (digits.length === 12 && digits.startsWith("91")) digits = digits.slice(2);
    else if (digits.length === 11 && digits.startsWith("0")) digits = digits.slice(1);
  }
  return digits.slice(0, maxDigits);
};

// Keystroke guard for a hardware keyboard: a non-digit character never lands in
// the field at all, and neither does an 11th digit. Editing and shortcut keys
// (Backspace, Tab, arrows, Ctrl/Cmd combos) all have key.length > 1 or a
// modifier, so they pass straight through.
const guardMobileKeyDown = (event, maxDigits) => {
  if (event.ctrlKey || event.metaKey || event.altKey) return; // copy / paste / select-all
  if (event.key.length > 1) return; // Backspace, Delete, Tab, Enter, arrows, F-keys…

  if (!/^[0-9]$/.test(event.key)) {
    event.preventDefault();
    return;
  }

  // Full already, and nothing selected for the digit to replace.
  const input = event.currentTarget;
  const filled = (input.value || "").replace(/\D/g, "").length;
  if (filled >= maxDigits && input.selectionStart === input.selectionEnd) {
    event.preventDefault();
  }
};

// Spread onto a mobile-number TextField: `{...mobileFieldProps()}`.
// Phones and tablets raise the number pad instead of the full keyboard, and a
// desktop keyboard yields nothing but digits. Pair it with sanitizeMobileInput
// in onChange — that is the guarantee, since some Android IMEs commit text
// without ever firing a keydown.
export const mobileFieldProps = (maxDigits = MOBILE_MAX_DIGITS) => ({
  type: "tel",
  inputProps: {
    inputMode: "numeric",
    pattern: "[0-9]*",
    autoComplete: "off",
    onKeyDown: (event) => guardMobileKeyDown(event, maxDigits),
  },
});

export const validateAadhar = (value) => {
  const cleaned = value?.toString().replace(/\D/g, "") || "";
  if (cleaned.length === 0) return { isValid: true, value: "", error: "" };
  if (cleaned.length > 12)
    return {
      isValid: false,
      value: cleaned.slice(0, 12),
      error: "Aadhar number must be exactly 12 digits",
    };
  if (cleaned.length === 12)
    return { isValid: true, value: cleaned, error: "" };
  return { isValid: true, value: cleaned, error: "" };
};

export const validatePAN = (value) => {
  const cleaned = value?.toString().toUpperCase() || "";
  const alphanumeric = cleaned.replace(/[^A-Z0-9]/g, "");
  if (alphanumeric.length === 0) return { isValid: true, value: "", error: "" };
  if (alphanumeric.length > 10)
    return {
      isValid: false,
      value: alphanumeric.slice(0, 10),
      error: "PAN card must be exactly 10 alphanumeric characters",
    };
  if (alphanumeric.length === 10) {
    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
    if (!panPattern.test(alphanumeric)) {
      return {
        isValid: true,
        value: alphanumeric,
        error: "Invalid PAN format (e.g., ABCDE1234F)",
      };
    }
    return { isValid: true, value: alphanumeric, error: "" };
  }
  return { isValid: true, value: alphanumeric, error: "" };
};

export const DEFAULT_PAWN_DOCUMENTS = [
  { name: "PAN", number: "", file: null, fileName: "", renewalDate: "" },
  { name: "Aadhar", number: "", file: null, fileName: "", renewalDate: "" },
  { name: "Photo", number: "", file: null, fileName: "", renewalDate: "" },
  { name: "Other", number: "", file: null, fileName: "", renewalDate: "" },
];

export const initialPawnCustomerState = {
  partyCode: "",
  mobile: "",
  name: "",
  dob: "",
  pickupAddress: "",
  relationType: "",
  relationName: "",
  annualIncome: "",
  note: "",
  documents: JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
};

// ── Initial state for a single nominee inside the dialog ──────────────────────
export const initialNomineeState = {
  name: "",
  mobile: "",
  dob: "",
  pickupAddress: "",
  relationType: "",
  relationName: "",
  annualIncome: "",
  note: "",
  partyCode: "",
  // junction fields
  relation: "",
  sharePercent: "",
  isPrimary: false,
  documents: JSON.parse(JSON.stringify(DEFAULT_PAWN_DOCUMENTS)),
};

// `short` is what the compact list header renders; `label` stays for anywhere
// that has room for the full wording.
export const SORT_OPTIONS = [
  { label: "A-Z", short: "A-Z", value: "ASC" },
  { label: "Z-A", short: "Z-A", value: "DESC" },
  { label: "Oldest First", short: "Oldest", value: "OLDEST" },
  { label: "Newest First", short: "Newest", value: "NEWEST" },
];

// Chips render on the blue gradient header: selected = solid white pill with
// blue text; unselected = translucent white outline.
export const glassChipSx = (isSelected) => ({
  fontWeight: isSelected ? 700 : 500,
  borderRadius: "10px",
  color: isSelected ? PRIMARY : "rgba(255,255,255,0.92)",
  backgroundColor: isSelected ? "#fff" : "rgba(255,255,255,0.14)",
  border: isSelected
    ? "1px solid #fff"
    : "1px solid rgba(255,255,255,0.3)",
  boxShadow: isSelected ? "0 3px 10px rgba(15,23,42,0.18)" : "none",
  transition: "all 0.25s ease",
  "&:hover": {
    backgroundColor: isSelected ? "#fff" : "rgba(255,255,255,0.28)",
  },
});

export const FilterLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: "#fff",
      fontWeight: 700,
      fontSize: "0.75rem",
      display: "block",
      mb: 0.75,
    }}
  >
    {children}
  </Typography>
);
